// components/AdminPanel.js
import React, { useState, useEffect } from 'react';
import { Base_Url } from '../config';
import * as LucideIcons from 'lucide-react';

const DynamicIcon = ({ name, size = 24, color, className }) => {
  if (!name) {
    console.warn('No icon name provided to DynamicIcon component');
    return null;
  }
 
  try {
    // Clean the icon name whether it's in JSX format or plain text
    let cleanIconName = name;
         
    // If it's in JSX format (e.g., <Computer />)
    if (name.startsWith('<') && name.endsWith('>')) {
      cleanIconName = name.replace(/[<>/]/g, '').trim();
    }
    // If it's just the name (e.g., Computer)
    else {
      cleanIconName = name.trim();
    }
 
    // Get the icon component from Lucide icons
    const IconComponent = LucideIcons[cleanIconName];
 
    if (!IconComponent) {
      console.warn(`Icon "${cleanIconName}" not found in Lucide icons`);
      return <LucideIcons.Computer size={size} color={color} className={className} />;
    }
 
    return <IconComponent size={size} color={color} className={className} />;
  } catch (error) {
    console.error('Error rendering Lucide icon:', error);
    return <LucideIcons.AlertCircle color="red" size={size} className={className} />;
  }
};

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('faculty');
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [formData, setFormData] = useState({ title: '', icon_class: '' });
  const [subFormData, setSubFormData] = useState({ title: '', link: '', section_id: '' });
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // API functions with better error handling
  const createAPI = (basePath) => ({
    getSections: () => fetch(`${Base_Url}/${basePath}-sections`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
    createSection: (data) => fetch(`${Base_Url}/${basePath}-sections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return res.json();
    }),
    updateSection: (id, data) => fetch(`${Base_Url}/${basePath}-sections/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return res.json();
    }),
    deleteSection: (id) => fetch(`${Base_Url}/${basePath}-sections/${id}`, {
      method: 'DELETE'
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return res.json();
    }),
    createSubsection: (data) => fetch(`${Base_Url}/${basePath}-subsections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return res.json();
    }),
    updateSubsection: (id, data) => fetch(`${Base_Url}/${basePath}-subsections/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return res.json();
    }),
    deleteSubsection: (id) => fetch(`${Base_Url}/${basePath}-subsections/${id}`, {
      method: 'DELETE'
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return res.json();
    })
  });

  const facultyJourneyAPI = createAPI('faculty');
  const studentJourneyAPI = createAPI('student');

  useEffect(() => {
    fetchSections();
  }, [activeTab]);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const api = activeTab === 'faculty' ? facultyJourneyAPI : studentJourneyAPI;
      // console.log(`Fetching ${activeTab} sections from: ${Base_Url}/${activeTab}-sections`);
      
      const response = await api.getSections();
      // console.log('API Response:', response);
      
      // The response should already be an array of sections with subsections
      // Ensure each section has a subsections array
      const formattedData = response.map(section => ({
        ...section,
        subsections: section.subsections || []
      }));
      
      setSections(formattedData);
      
      // Initialize expanded state for sections
      const initialExpandedState = {};
      formattedData.forEach(section => {
        initialExpandedState[section.id] = false;
      });
      setExpandedSections(initialExpandedState);
    } catch (error) {
      console.error('Error fetching sections:', error);
      alert(`Failed to fetch ${activeTab} data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const api = activeTab === 'faculty' ? facultyJourneyAPI : studentJourneyAPI;
      if (selectedSection) {
        await api.updateSection(selectedSection.id, formData);
        alert('Section updated successfully');
      } else {
        await api.createSection(formData);
        alert('Section created successfully');
      }
      resetForm();
      fetchSections();
    } catch (error) {
      console.error('Error saving section:', error);
      alert('Failed to save section');
    }
  };

  const handleSubsectionSubmit = async (e) => {
    e.preventDefault();
    try {
      const api = activeTab === 'faculty' ? facultyJourneyAPI : studentJourneyAPI;
      if (selectedSubsection) {
        // When updating, include all fields including section_id
        await api.updateSubsection(selectedSubsection.id, {
          title: subFormData.title,
          link: subFormData.link,
          section_id: subFormData.section_id // Add this line
        });
        alert('Subsection updated successfully');
      } else {
        await api.createSubsection(subFormData);
        alert('Subsection created successfully');
      }
      resetSubForm();
      fetchSections();
    } catch (error) {
      console.error('Error saving subsection:', error);
      alert('Failed to save subsection');
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Are you sure you want to delete this section? All its subsections will also be deleted.')) return;
    
    try {
      const api = activeTab === 'faculty' ? facultyJourneyAPI : studentJourneyAPI;
      await api.deleteSection(id);
      alert('Section deleted successfully');
      fetchSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      alert('Failed to delete section');
    }
  };

  const deleteSubsection = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subsection?')) return;
    
    try {
      const api = activeTab === 'faculty' ? facultyJourneyAPI : studentJourneyAPI;
      await api.deleteSubsection(id);
      alert('Subsection deleted successfully');
      fetchSections();
    } catch (error) {
      console.error('Error deleting subsection:', error);
      alert('Failed to delete subsection');
    }
  };

  const editSection = (section) => {
    setSelectedSection(section);
    setFormData({ title: section.title, icon_class: section.icon_class || '' });
  };

  const editSubsection = (subsection) => {
    setSelectedSubsection(subsection);
    setSubFormData({ 
      title: subsection.title, 
      link: subsection.link || '', 
      section_id: subsection.section_id 
    });
  };

  const resetForm = () => {
    setSelectedSection(null);
    setFormData({ title: '', icon_class: '' });
  };

  const resetSubForm = () => {
    setSelectedSubsection(null);
    setSubFormData({ title: '', link: '', section_id: '' });
  };

  return (
    <div className="container mt-4">
      <h2>Admin Panel</h2>
      
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'faculty' ? 'active' : ''}`}
            onClick={() => setActiveTab('faculty')}
          >
            Faculty Journey
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'student' ? 'active' : ''}`}
            onClick={() => setActiveTab('student')}
          >
            Student Journey
          </button>
        </li>
      </ul>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-md-5">
              <div className="card mb-4">
                <div className="card-header bg-light">
                  <h5>{selectedSection ? 'Edit Section' : 'Add New Section'}</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Icon Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.icon_class}
                        onChange={(e) => setFormData({...formData, icon_class: e.target.value})}
                        placeholder="e.g., User, Book, Computer"
                      />
                      <div className="form-text">
                        Use Lucide icon names (e.g., User, Book, Computer, GraduationCap, Users)
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-primary">
                        {selectedSection ? 'Update' : 'Create'} Section
                      </button>
                      {selectedSection && (
                        <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>

              <div className="card">
                <div className="card-header bg-light">
                  <h5>{selectedSubsection ? 'Edit Subsection' : 'Add New Subsection'}</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubsectionSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={subFormData.title}
                        onChange={(e) => setSubFormData({...subFormData, title: e.target.value})}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Link</label>
                      <input
                        type="text"
                        className="form-control"
                        value={subFormData.link}
                        onChange={(e) => setSubFormData({...subFormData, link: e.target.value})}
                        placeholder="/path/to/page"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <select
                        className="form-select"
                        value={subFormData.section_id}
                        onChange={(e) => setSubFormData({...subFormData, section_id: e.target.value})}
                        required
                        disabled={!!selectedSubsection}
                      >
                        <option value="">Select a section</option>
                        {sections.map(section => (
                          <option key={section.id} value={section.id}>
                            {section.title}
                          </option>
                        ))}
                      </select>
                      {selectedSubsection && (
                        <div className="form-text">
                          Section cannot be changed for existing subsections
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-primary">
                        {selectedSubsection ? 'Update' : 'Create'} Subsection
                      </button>
                      {(selectedSubsection || subFormData.section_id) && (
                        <button type="button" className="btn btn-outline-secondary" onClick={resetSubForm}>
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="col-md-7">
              <div className="card">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Sections</h5>
                  <span className="badge bg-primary">{sections.length}</span>
                </div>
                <div className="card-body p-0">
                  {sections.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">No sections found</p>
                    </div>
                  ) : (
                    <div className="list-group list-group-flush">
                      {sections.map(section => (
                        <div key={section.id} className="list-group-item">
                          <div 
                            className="d-flex justify-content-between align-items-center"
                            onClick={() => toggleSection(section.id)}
                            style={{cursor: 'pointer'}}
                          >
                            <div className="d-flex align-items-center">
                              {section.icon_class && (
                                <div className="me-2" style={{width: '20px'}}>
                                  <DynamicIcon name={section.icon_class} size={18} />
                                </div>
                              )}
                              <h6 className="mb-0">{section.title}</h6>
                            </div>
                            <div>
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editSection(section);
                                }}
                                title="Edit Section"
                              >
                                <LucideIcons.Edit size={14} />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSection(section.id);
                                }}
                                title="Delete Section"
                              >
                                <LucideIcons.Trash2 size={14} />
                              </button>
                              <LucideIcons.ChevronDown 
                                size={16}
                                className="ms-2"
                                style={{
                                  transform: expandedSections[section.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease'
                                }}
                              />
                            </div>
                          </div>
                          
                          {expandedSections[section.id] && (
                            <div className="mt-2 ps-4">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">Subsections:</h6>
                                <span className="badge bg-secondary">{section.subsections?.length || 0}</span>
                              </div>
                              
                              {section.subsections && section.subsections.length > 0 ? (
                                <div className="list-group">
                                  {section.subsections.map(sub => (
                                    <div key={sub.id} className="list-group-item py-2 px-3 d-flex justify-content-between align-items-center">
                                      <div className="flex-grow-1">
                                        <div className="fw-medium">{sub.title}</div>
                                        {sub.link && (
                                          <div className="text-muted small d-flex align-items-center">
                                            <LucideIcons.Link size={12} className="me-1" />
                                            {sub.link}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <button
                                          className="btn btn-sm btn-outline-primary me-1"
                                          onClick={() => editSubsection(sub)}
                                          title="Edit Subsection"
                                        >
                                          <LucideIcons.Edit size={12} />
                                        </button>
                                        <button
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => deleteSubsection(sub.id)}
                                          title="Delete Subsection"
                                        >
                                          <LucideIcons.Trash2 size={12} />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted small mb-0">No subsections for this section</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPanel;